import type { ReadCampaign } from '@tinct/core-api-models';

/**
 * The campaign creation funnel steps, in order. This is the single source of
 * truth for "where is a campaign in its setup". Both the funnel dispatcher
 * (which maps each step to a route) and the details page (setup stepper, action
 * menu) derive their state from this — do not re-implement the sequence elsewhere.
 */
export type CampaignSetupStep =
  | 'configure'
  | 'analyze'
  | 'building'
  | 'edit'
  | 'audience'
  | 'sampling'
  | 'preview'
  | 'publish'
  | 'publishing'
  | 'live';

export function getCampaignSetupStep(campaign?: ReadCampaign): CampaignSetupStep {
  if (campaign?.confirmedSteps?.includes('value_proposition') !== true) return 'configure';

  // NEW-origin campaigns build & edit a hosted page instead of the variant
  // phase; that phase (audience → … → publishing) is re-entered later from the
  // dashboard and never drives the setup step here.
  if (campaign?.landingPageOriginType === 'new') {
    const lp = campaign.landingPage;
    // `building` owns the failed state (it renders the error placeholder +
    // retry) and kicks off the first run itself when no page exists yet.
    // A page is editable only once `currentVersion` is set: block derivation
    // is an async listener that lands slightly after status === 'ready'.
    if (
      !lp ||
      lp.status === 'draft' ||
      lp.status === 'generating' ||
      lp.status === 'failed' ||
      !lp.currentVersion
    ) {
      return 'building';
    }
    if (campaign.published !== true && campaign.status !== 'published') return 'edit';
    return 'live';
  }

  if (campaign?.lastAnalysisJob?.status !== 'completed') return 'analyze';
  // ADWORD-axis campaigns have no audience/sampling phase: variants come from
  // synced ads campaigns (Google Ads / LinkedIn Ads), generated automatically on match.
  if (campaign?.landingPageVariationAxis !== 'adword') {
    if (campaign?.confirmedSteps?.includes('audience') !== true) return 'audience';
    if (campaign?.samplePages?.some((page) => page.status !== 'ready')) return 'sampling';
  }
  // The merged personalize & preview step confirms both the personalization
  // scope (block_identification) and the review (validation) on its Continue.
  if (
    campaign?.confirmedSteps?.includes('block_identification') !== true ||
    campaign?.confirmedSteps?.includes('validation') !== true
  ) return 'preview';
  if (campaign?.lastPublishedJob?.status === 'running') return 'publishing';
  if (campaign?.status === 'published') return 'live';
  if (campaign?.confirmedSteps?.includes('validation') === true) return 'publish';
  return 'live';
}

/**
 * True once the campaign is set up through the Preview & Refine validation step
 * (i.e. past every configuration gate) and is therefore publishable. For
 * NEW-origin campaigns that gate is the generated page being ready ('edit').
 */
export function isCampaignSetupComplete(campaign?: ReadCampaign): boolean {
  const step = getCampaignSetupStep(campaign);
  return step === 'publish' || step === 'publishing' || step === 'live' || step === 'edit';
}

const STUDIO_BLOCKED_REASONS: Partial<Record<CampaignSetupStep, string>> = {
  configure: 'Finish the campaign configuration first',
  analyze: 'The landing page is still being analysed',
  building: 'The landing page is still being generated',
};

/**
 * Why the studio can't open yet, or `null` once the blocks are known: an
 * existing page's analysis job completed, or a Tinct-generated page has its
 * current version (block derivation follows). Same source of truth as the
 * setup step, so it never disagrees with the funnel.
 */
export function getStudioBlockedReason(campaign?: ReadCampaign): string | null {
  return STUDIO_BLOCKED_REASONS[getCampaignSetupStep(campaign)] ?? null;
}

export function isStudioReady(campaign?: ReadCampaign): boolean {
  return getStudioBlockedReason(campaign) === null;
}

/**
 * The smallest campaign projection that decides `getStudioBlockedReason`:
 * every step past `building` is unblocked, so the later gates' fields
 * (audience, sampling, publication) are not needed.
 */
export const STUDIO_GATE_FIELDS = {
  confirmedSteps: null,
  landingPageOriginType: null,
  landingPage: {
    fields: {
      id: null,
      status: null,
      currentVersion: { fields: { id: null } },
    },
  },
  lastAnalysisJob: { fields: { status: null } },
} as const;
